import { useMemo, useState } from 'react';
import {
  Check,
  PanelRight,
  PanelBottom,
  EyeOff,
  Search,
  Moon,
  Sun,
  Monitor,
  Heart,
  type LucideIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { TOOLS } from '@/registry/tools';
import { usePrefs, type DockPosition } from '@/store/prefs';
import { setTheme as applyTheme, type Theme } from '@/lib/theme';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Settings is intentionally structured as a tabbed dialog so we can grow it
 * without re-architecting:
 *   - Dock      — position + tool picker (the only "interactive" config today)
 *   - Appearance — theme; future home for density / font size
 *   - About     — meta, credits, links
 *
 * Each section lives in its own small component. To add a new tab later,
 * write a component + add a TabsTrigger / TabsContent pair below.
 */
export function SettingsDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0">
        <header className="flex items-center justify-between border-b border-border px-4 pb-3 pt-4">
          <DialogTitle>Settings</DialogTitle>
        </header>

        <Tabs defaultValue="dock" className="flex flex-col">
          <TabsList className="mx-4 mt-3 grid h-auto grid-cols-3 gap-0.5 bg-secondary/40 p-0.5">
            <TabsTrigger value="dock" className="py-1.5 text-2xs">
              Dock
            </TabsTrigger>
            <TabsTrigger value="appearance" className="py-1.5 text-2xs">
              Appearance
            </TabsTrigger>
            <TabsTrigger value="about" className="py-1.5 text-2xs">
              About
            </TabsTrigger>
          </TabsList>

          <div className="min-h-[20rem] px-4 pb-4 pt-2">
            <TabsContent value="dock" className="mt-0 focus-visible:ring-0">
              <DockTab />
            </TabsContent>
            <TabsContent value="appearance" className="mt-0 focus-visible:ring-0">
              <AppearanceTab />
            </TabsContent>
            <TabsContent value="about" className="mt-0 focus-visible:ring-0">
              <AboutTab />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── Dock tab ─────────────────────────── */

const POSITION_OPTIONS: Array<{
  value: DockPosition;
  label: string;
  icon: LucideIcon;
}> = [
  { value: 'right', label: 'Right', icon: PanelRight },
  { value: 'bottom', label: 'Bottom', icon: PanelBottom },
  { value: 'hidden', label: 'Hidden', icon: EyeOff },
];

function DockTab() {
  const dockPosition = usePrefs((s) => s.dockPosition);
  const setDockPosition = usePrefs((s) => s.setDockPosition);
  const dockTools = usePrefs((s) => s.dockTools);
  const setDockTools = usePrefs((s) => s.setDockTools);

  const [query, setQuery] = useState('');

  const isInDock = (id: string) =>
    dockTools === null ? true : dockTools.includes(id);

  const toggle = (id: string) => {
    if (dockTools === null) {
      setDockTools(TOOLS.filter((t) => t.id !== id).map((t) => t.id));
      return;
    }
    if (dockTools.includes(id)) {
      setDockTools(dockTools.filter((x) => x !== id));
    } else {
      setDockTools([...dockTools, id]);
    }
  };

  const filteredTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TOOLS;
    return TOOLS.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.toLowerCase().includes(q)),
    );
  }, [query]);

  const selectedCount = TOOLS.filter((t) => isInDock(t.id)).length;

  return (
    <div className="flex flex-col gap-4">
      {/* Position */}
      <Group title="Position">
        <div className="grid grid-cols-3 gap-1.5">
          {POSITION_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = opt.value === dockPosition;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDockPosition(opt.value)}
                className={cn(
                  'flex min-w-0 flex-col items-center gap-1.5 rounded-md border px-2 py-2.5 text-xs transition-colors',
                  active
                    ? 'border-primary bg-primary/15 text-foreground shadow-sm'
                    : 'border-border bg-secondary/30 text-muted-foreground hover:bg-accent',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate font-medium">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </Group>

      {/* Tool picker */}
      <Group
        title="Tools"
        actions={
          <Badge variant="outline" className="font-mono">
            {selectedCount} / {TOOLS.length}
          </Badge>
        }
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter tools…"
            className="pl-7"
            aria-label="Filter tools"
          />
        </div>

        <div className="mt-1.5 flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDockTools(null)}
            className="text-2xs"
          >
            Select all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDockTools([])}
            className="text-2xs"
          >
            Clear
          </Button>
        </div>

        <div className="mt-1.5 max-h-56 overflow-y-auto overflow-x-hidden rounded-md border border-border bg-secondary/20">
          {filteredTools.length === 0 ? (
            <div className="px-3 py-4 text-center text-2xs italic text-muted-foreground">
              No tools match “{query}”
            </div>
          ) : (
            <ul role="listbox" className="divide-y divide-border">
              {filteredTools.map((tool) => {
                const Icon = tool.icon;
                const checked = isInDock(tool.id);
                return (
                  <li key={tool.id}>
                    <button
                      type="button"
                      onClick={() => toggle(tool.id)}
                      className={cn(
                        'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs transition-colors',
                        'hover:bg-accent',
                        checked && 'text-foreground',
                        !checked && 'text-muted-foreground',
                      )}
                    >
                      <CheckBox checked={checked} />
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {tool.name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Group>
    </div>
  );
}

/* ─────────────────────────── Appearance tab ─────────────────────────── */

const THEME_OPTIONS: Array<{
  value: Theme;
  label: string;
  icon: LucideIcon;
  hint: string;
}> = [
  { value: 'dark', label: 'Dark', icon: Moon, hint: 'Comfortable in dim rooms' },
  { value: 'light', label: 'Light', icon: Sun, hint: 'Bright surfaces' },
  { value: 'system', label: 'System', icon: Monitor, hint: 'Follow OS' },
];

function AppearanceTab() {
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);

  const handleSet = (t: Theme) => {
    setTheme(t);
    applyTheme(t);
  };

  return (
    <div className="flex flex-col gap-4">
      <Group title="Theme">
        <div className="grid grid-cols-1 gap-1.5 min-[380px]:grid-cols-3">
          {THEME_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = opt.value === theme;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSet(opt.value)}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition-colors min-[380px]:flex-col min-[380px]:items-center min-[380px]:gap-1 min-[380px]:text-center',
                  active
                    ? 'border-primary bg-primary/15 text-foreground shadow-sm'
                    : 'border-border bg-secondary/30 text-muted-foreground hover:bg-accent',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <div className="flex min-w-0 flex-col">
                  <span className="font-medium">{opt.label}</span>
                  <span className="truncate text-2xs opacity-70 min-[380px]:hidden">
                    {opt.hint}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </Group>
    </div>
  );
}

/* ─────────────────────────── About tab ─────────────────────────── */

function AboutTab() {
  return (
    <div className="flex flex-col gap-4">
      <Group title="DevUtils Sidebar">
        <div className="space-y-1 text-xs">
          <Row label="Version">
            <code className="font-mono">v0.1.0</code>
          </Row>
          <Row label="Tools">
            <span className="tabular-nums">{TOOLS.length} registered</span>
          </Row>
          <Row label="Storage">
            <span>chrome.storage.local · no cloud sync</span>
          </Row>
        </div>
      </Group>

      <Group title="Principles">
        <ul className="space-y-1 text-xs text-muted-foreground">
          <Bullet>Everything runs in your browser — nothing leaves it.</Bullet>
          <Bullet>No accounts, no telemetry, no AI dependencies.</Bullet>
          <Bullet>Keyboard-first (⌘K to search, ↑↓ to navigate).</Bullet>
        </ul>
      </Group>

      <Group title="Credit">
        <a
          href="https://khanhdang.name/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/30 px-2.5 py-1.5 text-xs transition-colors hover:border-primary/40 hover:bg-accent"
        >
          <Heart className="h-3 w-3 fill-current text-destructive" />
          Built by <span className="font-medium underline-offset-2 group-hover:underline">khanhdang.name</span>
        </a>
      </Group>
    </div>
  );
}

/* ─────────────────────────── Shared primitives ─────────────────────────── */

function Group({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0">
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {actions}
      </header>
      {children}
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-secondary/30 px-2.5 py-1.5">
      <span className="text-2xs uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-1.5">
      <span aria-hidden className="mt-1 inline-block h-1 w-1 shrink-0 rounded-full bg-primary" />
      <span>{children}</span>
    </li>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border transition-colors',
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background',
      )}
    >
      {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
    </span>
  );
}
